import { useTranslation } from 'next-i18next';

import { useMemo } from 'react';

import { Badge, Flex, Grid, GridItem, Heading, List, Text } from '@chakra-ui/react';

import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import { v4 as uuid } from 'uuid';

import { IconProps } from '@nocowanie/icons/common';
import { IconsLinear } from '@nocowanie/icons/linear';

import { getAmenityIcon } from '@app/helpers/get-amenity-icon';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import { useAppSelector } from '@app/state/state';

import { AmenitiesCategorizedProps } from './amenities-categorized.props';

export const AmenitiesCategorized = ({
    internalAmenityGroupsToShow,
    isSubsectionHidden = false,
    ...rest
}: AmenitiesCategorizedProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'amenities',
    });

    const { services } = useAppSelector(accommodationStaticSlice.selectors.selectAmenitiesData);

    const iconConfig: IconProps = {
        size: 'sm',
    };

    const groupedServices = useMemo(
        () =>
            map(
                groupBy(services, x => x?.serviceCategory?.name),
                (value, key) => ({
                    id: uuid(),
                    categoryName: key,
                    categoryItems: value.map(v => ({ id: uuid(), name: v.name })),
                }),
            ),
        [services],
    );

    if (!internalAmenityGroupsToShow?.length || !groupedServices?.length) {
        return;
    }

    return (
        <Grid
            templateColumns={{ base: '1fr', lg: isSubsectionHidden ? '1fr' : '3fr 8fr' }}
            gap={{ base: 'gutterWidth', lg: 'gutterWidthDesktop' }}
            {...rest}
        >
            {!isSubsectionHidden ? (
                <GridItem>
                    <Heading as="h3" size={'xs'} mb={2}>
                        {t('whatMakesUsStandOut')}
                    </Heading>
                    <Flex flexWrap={'wrap'} gap={2}>
                        {internalAmenityGroupsToShow.map(({ id, label, type, free }) => {
                            return (
                                <Badge
                                    key={id}
                                    display={'flex'}
                                    alignItems={'center'}
                                    variant={'outline'}
                                    colorPalette="green"
                                    gap={2}
                                    whiteSpace={'normal'}
                                    textAlign={'left'}
                                    size={{
                                        base: 'sm',
                                        md: 'md',
                                    }}
                                >
                                    {getAmenityIcon(type, iconConfig)}
                                    <Text as="span">
                                        {free ? `${t('free')} ${label.toLowerCase()}` : label}
                                    </Text>
                                </Badge>
                            );
                        })}
                    </Flex>
                </GridItem>
            ) : null}
            <GridItem
                columnGap={'gutterWidthDesktop'}
                css={{ columnCount: { base: '1', sm: '2' } }}
            >
                {groupedServices.map(({ id, categoryName, categoryItems }) => {
                    return (
                        <Flex
                            key={id}
                            flexDir={'column'}
                            gap={1}
                            pb={4}
                            css={{ breakInside: 'avoid' }}
                        >
                            <Heading
                                as="h3"
                                size={'xs'}
                                css={{ '&::first-letter': { textTransform: 'uppercase' } }}
                            >
                                {categoryName}
                            </Heading>
                            <List.Root gap={1} ml={1}>
                                {categoryItems.map(item => (
                                    <List.Item key={item.id} display={'flex'} alignItems={'center'}>
                                        <List.Indicator as={IconsLinear.Tick} />
                                        {item.name}
                                    </List.Item>
                                ))}
                            </List.Root>
                        </Flex>
                    );
                })}
            </GridItem>
        </Grid>
    );
};
